import { FALLBACK_LOCALE, localeToLanguage, normalizeLocale } from "./localeResolution.js";
import { resolveLocalizedField } from "./resolveLocalizedField.js";

export const CONTENT_PAGE_TRANSLATABLE_FIELDS = Object.freeze([
  "title",
  "slug",
  "excerpt",
  "bodyBlocksJson",
  "seoTitle",
  "seoDescription",
]);

export const COMMUNITY_TRANSLATABLE_FIELDS = Object.freeze([
  "displayName",
  "shortDescription",
  "description",
  "lifestyleDescription",
  "marketSummary",
  "seoTitle",
  "seoDescription",
]);

export function hasTranslationValue(value) {
  if (value == null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return true;
}

function readTranslationField(translations, locale, fieldName, fallbackLocale) {
  if (!translations || typeof translations !== "object") {
    return null;
  }
  const normalizedLocale = normalizeLocale(locale);
  const normalizedFallback = normalizeLocale(fallbackLocale);
  const language = localeToLanguage(normalizedLocale);

  const exact = translations[normalizedLocale]?.[fieldName];
  if (hasTranslationValue(exact)) {
    return exact;
  }

  for (const [loc, fields] of Object.entries(translations)) {
    if (localeToLanguage(loc) === language && hasTranslationValue(fields?.[fieldName])) {
      return fields[fieldName];
    }
  }

  const fallback = translations[normalizedFallback]?.[fieldName];
  if (hasTranslationValue(fallback)) {
    return fallback;
  }

  return null;
}

function readBaseEditorialField(record, fieldName) {
  if (!record || typeof record !== "object") {
    return null;
  }

  if (fieldName === "seoTitle") {
    return record.seoMetadataJson?.metaTitle || record.seoMetadataJson?.seoTitle || null;
  }
  if (fieldName === "seoDescription") {
    return record.seoMetadataJson?.metaDescription || record.seoMetadataJson?.seoDescription || null;
  }
  if (fieldName === "displayName") {
    return record.name || null;
  }
  if (fieldName === "shortDescription") {
    return record.shortDescription || record.description || null;
  }

  return record[fieldName] ?? null;
}

/**
 * Resolve a localized editorial field (string or JSON value such as bodyBlocksJson).
 */
export function resolveLocalizedEditorialValue(
  record,
  locale,
  fieldName,
  fallbackLocale = FALLBACK_LOCALE
) {
  const translated = readTranslationField(record?.translationsJson, locale, fieldName, fallbackLocale);
  if (hasTranslationValue(translated)) {
    return translated;
  }

  const adapted = {
    ...record,
    i18n: record?.translationsJson || record?.i18n || {},
  };
  const fromI18n = resolveLocalizedField(adapted, locale, fieldName, fallbackLocale, null);
  if (fromI18n != null && fromI18n !== "") {
    return fromI18n;
  }

  const base = readBaseEditorialField(record, fieldName);
  return hasTranslationValue(base) ? base : null;
}

export function resolveLocalizedEditorialString(
  record,
  locale,
  fieldName,
  fallbackLocale = FALLBACK_LOCALE,
  emptyFallback = ""
) {
  const value = resolveLocalizedEditorialValue(record, locale, fieldName, fallbackLocale);
  if (value == null) {
    return emptyFallback;
  }
  if (typeof value === "string") {
    return value;
  }
  return emptyFallback;
}

export function resolveLocalizedSlug(record, locale, fallbackLocale = FALLBACK_LOCALE) {
  return (
    resolveLocalizedEditorialString(record, locale, "slug", fallbackLocale, "") ||
    record?.slug ||
    ""
  );
}

/**
 * @returns {Record<string, string[]>} locale -> missing field names
 */
export function getMissingTranslations(
  content,
  supportedLocales,
  requiredFields,
  defaultLocale = FALLBACK_LOCALE
) {
  const missing = {};
  const locales = Array.isArray(supportedLocales) ? supportedLocales : [];
  const fields = Array.isArray(requiredFields) ? requiredFields : [];
  const normalizedDefault = normalizeLocale(defaultLocale);

  for (const locale of locales) {
    const normalizedLocale = normalizeLocale(locale);
    if (normalizedLocale === normalizedDefault) {
      continue;
    }

    const localeMissing = [];
    for (const fieldName of fields) {
      const baseValue = readBaseEditorialField(content, fieldName);
      if (!hasTranslationValue(baseValue)) {
        continue;
      }
      const translated = readTranslationField(content?.translationsJson, normalizedLocale, fieldName, normalizedDefault);
      if (!hasTranslationValue(translated)) {
        localeMissing.push(fieldName);
      }
    }

    if (localeMissing.length) {
      missing[normalizedLocale] = localeMissing;
    }
  }

  return missing;
}

/**
 * Apply locale resolution to a content page record for public rendering.
 */
export function localizeContentPageRecord(record, locale, fallbackLocale = FALLBACK_LOCALE) {
  if (!record) {
    return record;
  }

  const localizedSeoTitle = resolveLocalizedEditorialString(record, locale, "seoTitle", fallbackLocale);
  const localizedSeoDescription = resolveLocalizedEditorialString(
    record,
    locale,
    "seoDescription",
    fallbackLocale
  );

  return {
    ...record,
    title: resolveLocalizedEditorialString(record, locale, "title", fallbackLocale, record.title),
    slug: resolveLocalizedSlug(record, locale, fallbackLocale),
    excerpt: resolveLocalizedEditorialString(record, locale, "excerpt", fallbackLocale, record.excerpt || ""),
    bodyBlocksJson:
      resolveLocalizedEditorialValue(record, locale, "bodyBlocksJson", fallbackLocale) ||
      record.bodyBlocksJson ||
      [],
    seoMetadataJson: {
      ...(record.seoMetadataJson || {}),
      ...(localizedSeoTitle ? { metaTitle: localizedSeoTitle } : {}),
      ...(localizedSeoDescription ? { metaDescription: localizedSeoDescription } : {}),
    },
    activeLocale: normalizeLocale(locale),
  };
}

/**
 * Apply locale resolution to a community record for public rendering.
 */
export function localizeCommunityRecord(record, locale, fallbackLocale = FALLBACK_LOCALE) {
  if (!record) {
    return record;
  }

  const displayName = resolveLocalizedEditorialString(record, locale, "displayName", fallbackLocale);
  const localizedSeoTitle = resolveLocalizedEditorialString(record, locale, "seoTitle", fallbackLocale);
  const localizedSeoDescription = resolveLocalizedEditorialString(
    record,
    locale,
    "seoDescription",
    fallbackLocale
  );

  return {
    ...record,
    name: displayName || record.name,
    slug: resolveLocalizedSlug(record, locale, fallbackLocale),
    description:
      resolveLocalizedEditorialString(record, locale, "description", fallbackLocale, record.description || "") ||
      resolveLocalizedEditorialString(record, locale, "shortDescription", fallbackLocale, ""),
    lifestyleDescription: resolveLocalizedEditorialString(
      record,
      locale,
      "lifestyleDescription",
      fallbackLocale,
      record.lifestyleDescription || ""
    ),
    seoMetadataJson: {
      ...(record.seoMetadataJson || {}),
      ...(localizedSeoTitle ? { metaTitle: localizedSeoTitle, seoTitle: localizedSeoTitle } : {}),
      ...(localizedSeoDescription
        ? { metaDescription: localizedSeoDescription, seoDescription: localizedSeoDescription }
        : {}),
    },
    activeLocale: normalizeLocale(locale),
  };
}
